import React, { useContext, useState } from 'react';
import UserContext from '../../../Context/UserProvider';
import { useApiService } from '../../../services/ApiService/ApiService';
import {
    Box,
    Container,
    Paper,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    IconButton,
    Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface AddEmailSurveyProps {
    error?: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
}

function AddEmailSurvey({ error }: AddEmailSurveyProps) {
    const { userInfo } = useContext(UserContext);
    const { fetchWithAuth } = useApiService();
    const [modalIsOpen, setIsOpen] = useState(false);
    const [emails, setEmails] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEmails(event.target.value);
    };

    const submitForm = async () => {
        if (!emails.trim()) return;
        
        setIsSubmitting(true);
        try {
            const response = await fetchWithAuth('/survey/create', {
                method: 'POST',
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    fleetUuid: userInfo.fleetUuid,
                    emails: emails
                })
            });

            if (response.ok) {
                setEmails('');
                setIsOpen(true);
            }
        } catch (error) {
            console.error('Error sending surveys:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Container 
            maxWidth={false}
            sx={{ 
                py: 3,
                width: {
                    xs: '100%',    // Full width on mobile
                    sm: '100%',    // Full width on tablet
                    md: '66.67%',  // 2/3 width on medium screens
                    lg: '33.33%',  // 1/3 width on large screens
                },
                mx: 'auto'
            }}
        >
            <Paper 
                elevation={0} 
                sx={{ 
                    borderRadius: 1,
                    overflow: 'hidden',
                    p: 3
                }}
            >
                <Stack spacing={3}>
                    <Typography variant="h6">
                        Send Survey Emails
                    </Typography>
                    
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={emails}
                        onChange={handleEmailChange}
                        placeholder="Enter email addresses separated by semicolons (;)&#10;Example: jake@evata.co.uk; john@evata.co.uk"
                        variant="outlined"
                    />

                    <Button
                        variant="contained"
                        onClick={submitForm}
                        disabled={isSubmitting || !emails.trim()}
                        fullWidth
                    >
                        Send Surveys
                    </Button>
                </Stack>
            </Paper>

            <Dialog
                open={modalIsOpen}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            Success
                        </Typography>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography align="center" variant="body1" sx={{ my: 2 }}>
                        The User Survey emails have been sent successfully.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default AddEmailSurvey;
